/**
 * The per-stop start / duration / notes dialog, lifted out of ItineraryEditor
 * so that file stays closer to the repo's component-size budget. Purely
 * presentational: it holds no draft state of its own and performs no mutation.
 * The editor owns the draft and decides whether a Save is valid (an
 * unparseable duration aborts the save and leaves this modal open).
 *
 * Form convention (repo): action buttons TOP RIGHT, <div> not <form>,
 * submission via button onClick.
 */

import { Button } from '../ui/Button';

export interface StopDraft {
  dayId: string;
  stopId: string;
  notes: string;
  arrivalTime: string;
  /** Bound as a string for the number input; the editor parses it on save. */
  durationMinutes: string;
}

interface StopDetailsModalProps {
  draft: StopDraft;
  onChange: (draft: StopDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const StopDetailsModal = ({
  draft,
  onChange,
  onSave,
  onCancel,
}: StopDetailsModalProps) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
    role="dialog"
    aria-modal="true"
    aria-label="Edit stop details"
  >
    <div className="w-full max-w-md rounded-lg bg-gray-50 p-4 shadow-xl">
      <div className="mb-4 flex justify-end gap-2">
        <Button size="sm" variant="primary" onClick={onSave}>
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[0.7rem] uppercase tracking-widest text-gray-500">
            Start
          </span>
          <input
            type="time"
            value={draft.arrivalTime}
            onChange={(e) => onChange({ ...draft, arrivalTime: e.target.value })}
            className="rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[0.7rem] uppercase tracking-widest text-gray-500">
            Duration (min)
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={24 * 60}
            step={15}
            value={draft.durationMinutes}
            onChange={(e) =>
              onChange({ ...draft, durationMinutes: e.target.value })
            }
            placeholder="e.g. 90"
            className="rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info-500"
          />
        </label>
      </div>
      <label className="mt-3 block">
        <span className="text-[0.7rem] uppercase tracking-widest text-gray-500">
          Notes
        </span>
        <textarea
          autoFocus
          value={draft.notes}
          onChange={(e) => onChange({ ...draft, notes: e.target.value })}
          rows={4}
          className="mt-1 w-full rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info-500"
          placeholder="Notes for this stop…"
        />
      </label>
    </div>
  </div>
);
